import { combineReducers } from "redux";
import {
  ProcessActionTypes,
  ProcessActionType,
  WorkflowState,
  ProcessUIState,
} from "./types";
import { OfficeType, StepStatus } from "../../types/process";

const initialWorkflowState: WorkflowState = {
  categories: [],
  loading: false,
  error: null,
};

const initialUIState: ProcessUIState = {
  selectedOfficeType: OfficeType.ALL,
  selectedStep: null,
  isStepDetailModalOpen: false,
  feedbackTarget: null,
  isFeedbackModalOpen: false,
  isCanvasViewOpen: false,
  canvasViewCategory: null,
};

// Workflows Reducer
const workflowsReducer = (
  state = initialWorkflowState,
  action: ProcessActionType,
): WorkflowState => {
  switch (action.type) {
    case ProcessActionTypes.FETCH_WORKFLOWS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ProcessActionTypes.FETCH_WORKFLOWS_SUCCESS:
      return {
        ...state,
        loading: false,
        categories: action.payload,
        error: null,
      };

    case ProcessActionTypes.FETCH_WORKFLOWS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case ProcessActionTypes.SUBMIT_STEP_CONFIG_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ProcessActionTypes.SUBMIT_STEP_CONFIG_SUCCESS:
      return {
        ...state,
        loading: false,
        // Don't update categories from API response - keep optimistic update
        error: null,
      };

    case ProcessActionTypes.SUBMIT_STEP_CONFIG_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case ProcessActionTypes.SUBMIT_FEEDBACK_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ProcessActionTypes.SUBMIT_FEEDBACK_SUCCESS:
      return {
        ...state,
        loading: false,
        // Don't update categories from API response - keep optimistic update
        error: null,
      };

    case ProcessActionTypes.SUBMIT_FEEDBACK_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case ProcessActionTypes.DELETE_STEP_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ProcessActionTypes.DELETE_STEP_SUCCESS:
      return {
        ...state,
        loading: false,
        // Don't update categories from API response - keep optimistic update
        error: null,
      };

    case ProcessActionTypes.DELETE_STEP_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case ProcessActionTypes.OPTIMISTIC_UPDATE_FEEDBACK: {
      const { targetType, targetId, feedbackEntry } = action.payload;
      const updatedCategories = state.categories.map(category => {
        if (targetType === "category" && category.id === targetId) {
          return {
            ...category,
            feedbackHistory: [
              ...(category.feedbackHistory || []),
              feedbackEntry,
            ],
            hasFeedback: true,
          };
        }
        return {
          ...category,
          processes: category.processes.map(process => ({
            ...process,
            steps: process.steps.map(step => {
              if (targetType === "step" && step.id === targetId) {
                return {
                  ...step,
                  feedbackHistory: [
                    ...(step.feedbackHistory || []),
                    feedbackEntry,
                  ],
                  hasFeedback: true,
                };
              }
              return step;
            }),
          })),
        };
      });
      return {
        ...state,
        categories: updatedCategories,
      };
    }

    case ProcessActionTypes.OPTIMISTIC_UPDATE_STEP_CONFIG: {
      const { categoryId, processId, stepId, config } = action.payload;
      const updatedCategories = state.categories.map(category => {
        if (category.id === categoryId) {
          return {
            ...category,
            processes: category.processes.map(process => {
              if (process.id === processId) {
                return {
                  ...process,
                  steps: process.steps.map(step => {
                    if (step.id === stepId) {
                      return {
                        ...step,
                        currentConfig: config,
                        status: StepStatus.COMPLETE,
                        requiresConfiguration: false,
                      };
                    }
                    return step;
                  }),
                };
              }
              return process;
            }),
          };
        }
        return category;
      });
      return {
        ...state,
        categories: updatedCategories,
      };
    }

    case ProcessActionTypes.OPTIMISTIC_DELETE_STEP: {
      const { categoryId, processId, stepId } = action.payload;
      const updatedCategories = state.categories.map(category => {
        if (category.id === categoryId) {
          return {
            ...category,
            processes: category.processes.map(process => {
              if (process.id === processId) {
                return {
                  ...process,
                  steps: process.steps.filter(step => step.id !== stepId),
                };
              }
              return process;
            }),
          };
        }
        return category;
      });
      return {
        ...state,
        categories: updatedCategories,
      };
    }

    default:
      return state;
  }
};

// Process UI Reducer
const processUiReducer = (
  state = initialUIState,
  action: ProcessActionType,
): ProcessUIState => {
  switch (action.type) {
    case ProcessActionTypes.SET_SELECTED_OFFICE_TYPE:
      return {
        ...state,
        selectedOfficeType: action.payload,
      };

    case ProcessActionTypes.OPEN_STEP_DETAIL_MODAL:
      return {
        ...state,
        selectedStep: action.payload,
        isStepDetailModalOpen: true,
      };

    case ProcessActionTypes.CLOSE_STEP_DETAIL_MODAL:
      return {
        ...state,
        isStepDetailModalOpen: false,
        selectedStep: null,
      };

    case ProcessActionTypes.OPEN_FEEDBACK_MODAL:
      return {
        ...state,
        feedbackTarget: action.payload,
        isFeedbackModalOpen: true,
      };

    case ProcessActionTypes.CLOSE_FEEDBACK_MODAL:
      return {
        ...state,
        isFeedbackModalOpen: false,
        feedbackTarget: null,
      };

    case ProcessActionTypes.OPEN_CANVAS_VIEW_MODAL:
      return {
        ...state,
        canvasViewCategory: action.payload,
        isCanvasViewOpen: true,
      };

    case ProcessActionTypes.CLOSE_CANVAS_VIEW_MODAL:
      return {
        ...state,
        isCanvasViewOpen: false,
        canvasViewCategory: null,
      };

    default:
      return state;
  }
};

// Combined Process Reducer
const processReducer = combineReducers({
  workflows: workflowsReducer,
  processUi: processUiReducer,
});

export default processReducer;
